import {configureStore, type Middleware} from '@reduxjs/toolkit';
import {logger} from 'redux-logger';
import {load, save} from 'redux-localstorage-simple';

import {reducer as baseUrl} from './baseUrl';
import {reducer as auth} from './auth';
import {reducer as device, sw} from './device';
import {Syncer} from '../util/sync';
import * as log from '../util/log';

/**
 * Namespace in local storage where Nudge state is persisted.
 */
const NUDGE_NS = 'scpl:nudge';

/**
 * Format an access token for use in HTTP headers.
 */
const formatAccessToken = (tokenType: string, token: string) => {
  if (!tokenType || !token) {
    return '';
  }
  return `${tokenType} ${token}`;
};

/**
 * Daemon to keep the access token in sync with the service worker.
 */
const swSyncer = new Syncer(
  async () => {
    const {tokenType, token} = store.getState().auth;
    return formatAccessToken(tokenType, token);
  },
  async (value) => {
    try {
      await sw.updateAccessToken(value);
      log.debug('Service worker access token synced');
    } catch (e) {
      log.error('Failed to update service worker access token', e as Error);
    }
  },
);

/**
 * Middleware to sync access token with service worker.
 * TODO: we can move all access token stuff to the service worker and then
 * we wouldn't have to do this, plus the requests would be more secure.
 */
const syncState: Middleware =
  ({getState}) =>
  (next) =>
  (action) => {
    const result = next(action);
    const state = getState();
    const {tokenType, token} = state.auth;

    swSyncer
      .force(formatAccessToken(tokenType, token))
      .catch((e) =>
        log.error('Failed to update service worker access token', e),
      );
    return result;
  };

/**
 * Middleware to persist state.
 */
const saveState = save({namespace: NUDGE_NS});

/**
 * Application state (Redux store).
 */
export const store = configureStore({
  preloadedState: load({namespace: NUDGE_NS}),
  middleware: (getDefaultMiddleware) =>
    // NB: Use `.concat` to add more middleware instead of the spread operator.
    // Typescript will lose type information with the spread operator, which
    // will cause the typed version of `AppDispatch` below to be incorrect.
    getDefaultMiddleware()
      .concat(logger as typeof syncState)
      .concat(saveState as typeof syncState)
      .concat(syncState),
  reducer: {
    baseUrl,
    auth,
    device,
  },
});

export type AppStore = typeof store;

export type AppDispatch = typeof store.dispatch;

export type RootState = ReturnType<typeof store.getState>;
